/**
 * How Stork Rentals Works Section — Home Page
 *
 * Two-card layout explaining the two rental types available:
 * Short-Term (fixed dates) and Flexible Rolling (monthly, open-ended).
 * CMS-ready: content fetched from Backoffice when configured, falls back to defaults.
 */

import { usePageSection } from '@/hooks/usePageContent';

interface RentalTypeCard {
    title: string;
    description: string;
    detail: string;
}

export interface HowItWorksContent {
    heading: string;
    subheading: string;
    rentalTypes: RentalTypeCard[];
}

const DEFAULT_HOW_IT_WORKS: HowItWorksContent = {
    heading: 'How Stork Rentals Works',
    subheading: 'We can help you decide what kind of rental you need! There are two options to choose from.',
    rentalTypes: [
        {
            title: 'Short-Term Rentals',
            description:
                'Short-term rentals require specific pickup and return dates, making them perfect for holidays, travel, or when you have little visitors and know exactly how long you need the items.',
            detail: 'Rentals range from 1 day up to a maximum of one month. You are charged at checkout for the entire rental duration.',
        },
        {
            title: 'Flexible, Rolling Rentals',
            description:
                'Flexible rolling rentals are for when you want an item for more than a month, or for an unspecified period. Only a start date is required.',
            detail: 'The rental rolls over with a set monthly charge until the item is returned and the rental is closed.',
        },
    ],
};

export const HowItWorksSection = () => {
    const cms = usePageSection<HowItWorksContent>('home', 'how-it-works');
    const content = cms ?? DEFAULT_HOW_IT_WORKS;

    return (
        <section
            aria-labelledby="how-it-works-heading"
            className="bg-[linear-gradient(180deg,rgba(246,246,233,1)_0%,rgba(255,255,255,1)_100%)] py-16 sm:py-24"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                {/* Section heading */}
                <div className="delight-rise mb-12 sm:mb-16">
                    <h2
                        id="how-it-works-heading"
                        className="font-display text-5xl leading-[0.94] tracking-tight text-foreground sm:text-6xl"
                    >
                        {content.heading}
                    </h2>
                    <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
                        {content.subheading}
                    </p>
                </div>

                {/* Rental type cards */}
                <div className="delight-rise delight-delay-1 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {content.rentalTypes.map((type, index) => (
                        <div
                            key={index}
                            data-testid="rental-type-card"
                            className="flex flex-col rounded-xl border border-border bg-white p-6 transition-[transform,box-shadow,border-color] duration-200 hover:border-primary/20 hover:shadow-[0_18px_34px_rgba(54,49,43,0.08)] motion-safe:hover:-translate-y-1 sm:p-8"
                        >
                            <h3 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
                                {type.title}
                            </h3>
                            <p className="mt-3 text-sm leading-7 text-muted-foreground sm:text-base">
                                {type.description}
                            </p>
                            <p className="mt-3 text-sm leading-7 text-foreground/70 sm:text-base">
                                {type.detail}
                            </p>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default HowItWorksSection;
